/**
 * DeploymentTokensModal Component
 *
 * Modal for viewing and managing deployment tokens for an application.
 * Allows creating new tokens and revoking existing ones.
 */

import React, { useState } from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Alert } from '../../../components/ui/Alert';
import { CopyableCode } from '../../../components/ui/CopyableCode';
import { useDeploymentTokens } from '../hooks/useDeploymentTokens';
import { CreateDeploymentTokenModal } from './CreateDeploymentTokenModal';
import { RevokeDeploymentTokenModal } from './RevokeDeploymentTokenModal';
import type { DeploymentTokenSanitized } from '../../../types/deployment-token.types';
import { DeploymentTokenStatus } from '../../../types/deployment-token.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface DeploymentTokensModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  applicationId: string;
  applicationName: string;
}

/**
 * Format relative time
 */
const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return date.toLocaleDateString();
};

/**
 * Check if token is expired
 */
const isTokenExpired = (expiresAt?: string): boolean => {
  if (!expiresAt) return false;
  return new Date(expiresAt).getTime() < Date.now();
};

/**
 * DeploymentTokensModal component
 */
export const DeploymentTokensModal: React.FC<DeploymentTokensModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  applicationId,
  applicationName,
}) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [tokenToRevoke, setTokenToRevoke] = useState<DeploymentTokenSanitized | null>(null);

  const { data: tokens, isLoading, error, refetch } = useDeploymentTokens(
    tenantId,
    applicationId
  );

  const activeTokens = tokens?.filter(t => t.status === DeploymentTokenStatus.ACTIVE) || [];
  const revokedTokens = tokens?.filter(t => t.status === DeploymentTokenStatus.REVOKED) || [];

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    refetch();
  };

  const handleRevokeSuccess = () => {
    setTokenToRevoke(null);
    refetch();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <Modal.Header>
          <div className="flex items-center justify-between w-full">
            <span>Deployment Tokens</span>
          </div>
        </Modal.Header>

        <Modal.Body>
          <div className="space-y-4">
            {/* Header with info and create button */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">
                  Manage deployment tokens for <strong>{applicationName}</strong>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Tokens are used to authenticate CI/CD pipelines for automated deployments.
                </p>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowCreateModal(true)}
              >
                Create Token
              </Button>
            </div>

            {/* Error Alert */}
            {error && (
              <Alert variant="error">
                {getErrorMessage(error)}
              </Alert>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            )}

            {/* Active Tokens */}
            {!isLoading && activeTokens.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-2">
                  Active Tokens ({activeTokens.length})
                </h3>
                <div className="space-y-2">
                  {activeTokens.map((token) => (
                    <div
                      key={token.tokenId}
                      className="border border-gray-200 rounded-lg p-4 bg-white hover:bg-gray-50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-medium text-gray-900 truncate">
                              {token.tokenName}
                            </h4>
                            {isTokenExpired(token.expiresAt) ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                Expired
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                Active
                              </span>
                            )}
                          </div>
                          <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                            <span>Code:</span>
                            <CopyableCode value={token.tokenCode} />
                            <span>|</span>
                            <span>Prefix: <code className="bg-gray-100 px-1 rounded">{token.tokenPrefix}...</code></span>
                          </div>
                          <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                            <span>Created {formatRelativeTime(token.createdAt)}</span>
                            {token.lastUsedAt && (
                              <span>Last used {formatRelativeTime(token.lastUsedAt)}</span>
                            )}
                            <span>{token.usageCount} uses</span>
                            {token.expiresAt && (
                              <span>
                                Expires {new Date(token.expiresAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => setTokenToRevoke(token)}
                        >
                          Revoke
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {!isLoading && activeTokens.length === 0 && (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No active tokens</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Create a deployment token to enable CI/CD deployments.
                </p>
                <div className="mt-4">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setShowCreateModal(true)}
                  >
                    Create Token
                  </Button>
                </div>
              </div>
            )}

            {/* Revoked Tokens (Collapsed) */}
            {!isLoading && revokedTokens.length > 0 && (
              <details className="group">
                <summary className="cursor-pointer text-sm font-medium text-gray-500 hover:text-gray-700">
                  Revoked Tokens ({revokedTokens.length})
                </summary>
                <div className="mt-2 space-y-2">
                  {revokedTokens.map((token) => (
                    <div
                      key={token.tokenId}
                      className="border border-gray-200 rounded-lg p-4 bg-gray-50 opacity-60"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-medium text-gray-600 truncate line-through">
                              {token.tokenName}
                            </h4>
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600">
                              Revoked
                            </span>
                          </div>
                          <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                            <span>Code:</span>
                            <CopyableCode value={token.tokenCode} />
                          </div>
                          <div className="mt-2 flex items-center gap-4 text-xs text-gray-400">
                            <span>Created {formatRelativeTime(token.createdAt)}</span>
                            {token.revokedAt && (
                              <span>Revoked {formatRelativeTime(token.revokedAt)}</span>
                            )}
                            <span>{token.usageCount} uses</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create Token Modal */}
      <CreateDeploymentTokenModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        tenantId={tenantId}
        applicationId={applicationId}
        applicationName={applicationName}
      />

      {/* Revoke Token Modal */}
      {tokenToRevoke && (
        <RevokeDeploymentTokenModal
          isOpen={true}
          onClose={() => setTokenToRevoke(null)}
          tenantId={tenantId}
          applicationId={applicationId}
          token={tokenToRevoke}
          onSuccess={handleRevokeSuccess}
        />
      )}
    </>
  );
};
